import { useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Button } from '../ui-blocks/Button'
import { CountryCodeSelect } from '../ui-blocks/CountryCodeSelect'
import { detectCountryCode } from '../../constants/countries'
import { ANALYTICS_EVENTS, track } from '../../lib/analytics'
import { fetchLocation } from '../../lib/geolocation'
import { invalidPhoneMessage, isValidPhoneForCountry, normalisePhone } from '../../lib/phone'
import { supabase } from '../../lib/supabaseClient'

type Status = 'success' | 'error' | ''

type Option = { value: string; label: string }

const CHAIR_TYPES: Option[] = [
  { value: 'auditorium', label: 'Auditorium' },
  { value: 'sports', label: 'Sports' },
  { value: 'other', label: 'Other' },
]

const ENVIRONMENTS: Option[] = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'both', label: 'Both' },
]

// Both fields are optional, so clicking the selected button clears it again.
const ToggleGroup = ({
  label,
  options,
  value,
  onSelect,
}: {
  label: string
  options: Option[]
  value: string
  onSelect: (value: string) => void
}) => (
  <div className="flex flex-col gap-2">
    <span className="text-sm text-gray-600">{label}</span>
    <div role="group" aria-label={label} className="flex rounded-lg border border-gray-300 p-1">
      {options.map(o => {
        const active = value === o.value
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(active ? '' : o.value)}
            className={twMerge(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-my-orange',
              active ? 'bg-orange-50 text-my-orange' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
            )}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  </div>
)

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  message: '',
  auditorium: '',
  seats: '',
  environment: '',
}

// Best-effort and fire-and-forget: the enquiry is already saved, so a failed
// subscribe shouldn't surface as a form error.
const subscribeToNewsletter = async (name: string, email: string, surface: string) => {
  const [firstName, ...rest] = name.trim().split(/\s+/)
  try {
    const { data, error } = await supabase.functions.invoke<{
      ok?: boolean
      error?: string
      alreadySubscribed?: boolean
      welcomeEmailSent?: boolean
    }>('newsletter-subscribe', {
      body: { email: email.trim(), firstName, lastName: rest.join(' '), source: 'contact_form' },
    })
    if (error || !data?.ok) {
      track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_FAILED, {
        source: 'contact_form',
        surface,
        reason: error ? 'invoke_error' : 'subscribe_error',
        detail: data?.error || error?.message,
      })
      return
    }
    track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_SUCCEEDED, {
      source: 'contact_form',
      surface,
      alreadySubscribed: !!data.alreadySubscribed,
      welcomeEmailSent: !!data.welcomeEmailSent,
    })
  } catch (err) {
    track(ANALYTICS_EVENTS.NEWSLETTER_SUBSCRIBE_FAILED, {
      source: 'contact_form',
      surface,
      reason: 'unexpected_error',
      detail: err instanceof Error ? err.message : String(err),
    })
  }
}

const inputClass =
  'rounded-lg border border-gray-300 p-3 focus:outline-none focus:ring-2 focus:ring-my-orange'

type Props = {
  // Catalog key of the chair being enquired about, stored as product_id.
  productId?: string
  // The auditorium / indoor-outdoor toggles. Off for product enquiries, where
  // the chosen chair already answers both.
  showProjectFields?: boolean
  // Where the form is being rendered, so funnels can be split by entry point.
  surface?: string
  onSuccess?: () => void
  className?: string
}

export const ContactForm = ({
  productId,
  showProjectFields = true,
  surface = 'contact_page',
  onSuccess,
  className,
}: Props) => {
  // country_code is the ISO code (e.g. 'IN'); the dial code is derived from it.
  const [form, setForm] = useState({ ...EMPTY_FORM, country_code: detectCountryCode() as string })

  // Kept out of `form` since that object is inserted into contact_submissions as-is.
  const [subscribe, setSubscribe] = useState(true)

  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<Status>('')
  const [phoneError, setPhoneError] = useState('')
  const [formError, setFormError] = useState('')
  const startedRef = useRef(false)

  useEffect(() => {
    if (status) {
      const t = setTimeout(() => setStatus(''), 5000)
      return () => clearTimeout(t)
    }
  }, [status])

  const blurredRef = useRef<Set<string>>(new Set())

  // Started on first interaction rather than on page load, so the IP lookup only
  // happens for people actually sending an enquiry. Held as the in-flight promise
  // so a fast typist's submit waits for it instead of losing it.
  const locationRef = useRef<Promise<string> | null>(null)
  const lookUpLocation = () => {
    if (!locationRef.current) locationRef.current = fetchLocation()
    return locationRef.current
  }

  const onChange = (key: string, value: string) => {
    if (!startedRef.current) {
      startedRef.current = true
      track(ANALYTICS_EVENTS.CONTACT_FORM_STARTED, { first_field: key, surface, product_id: productId })
      lookUpLocation()
    }
    setFormError('')
    setForm(prev => ({ ...prev, [key]: value }))
  }

  // Track which fields users actually completed (no PII — only length + filled status).
  // Fires once per field per form attempt so we don't flood events on repeated blurs.
  const onBlurField = (key: string, value: string) => {
    if (blurredRef.current.has(key)) return
    blurredRef.current.add(key)
    track(ANALYTICS_EVENTS.CONTACT_FORM_FIELD_BLURRED, {
      field: key,
      filled: value.trim().length > 0,
      length: value.trim().length,
      surface,
    })
  }

  // Toggle buttons never blur the way the inputs do, so record the choice here
  // to keep the per-field completion tracking intact.
  const onSelectToggle = (key: string, value: string) => {
    onChange(key, value)
    onBlurField(key, value)
  }

  const validatePhone = (phone: string, country: string) => {
    const message = phone.trim() && !isValidPhoneForCountry(phone, country)
      ? invalidPhoneMessage(country)
      : ''
    setPhoneError(message)
    return !message
  }

  // Validate on blur rather than on every keystroke, so a half-typed number
  // isn't flagged as wrong while it's still being entered.
  const onPhoneBlur = () => {
    onBlurField('phone', form.phone)
    const normalised = normalisePhone(form.phone, form.country_code)
    const country = normalised?.country ?? form.country_code
    const phone = normalised?.nationalNumber ?? form.phone
    if (normalised) setForm(prev => ({ ...prev, phone, country_code: country }))
    validatePhone(phone, country)
  }

  const onCountryChange = (code: string) => {
    onChange('country_code', code)
    // Same digits can be valid in one country and not another — re-check, but
    // only once they've finished with the phone field at least once.
    if (phoneError || blurredRef.current.has('phone')) validatePhone(form.phone, code)
  }

  const onSubmit = useCallback(async () => {
    const { name, email, phone, seats } = form
    if (!name || !email || !phone || !seats) {
      const missing = ['name', 'email', 'phone', 'seats'].filter(k => !form[k as keyof typeof form])
      track(ANALYTICS_EVENTS.CONTACT_FORM_FAILED, {
        reason: 'missing_required_fields',
        missing_fields: missing.join(','),
        surface,
      })
      setFormError('Please fill all required fields.')
      return
    }

    if (!isValidPhoneForCountry(phone, form.country_code)) {
      setPhoneError(invalidPhoneMessage(form.country_code))
      track(ANALYTICS_EVENTS.CONTACT_FORM_FAILED, {
        reason: 'invalid_phone',
        country_code: form.country_code,
        surface,
      })
      return
    }

    setLoading(true)
    setStatus('')
    setFormError('')

    track(ANALYTICS_EVENTS.CONTACT_FORM_SUBMITTED, {
      auditorium: form.auditorium || undefined,
      environment: form.environment || undefined,
      has_seats_field: !!form.seats,
      country_code: form.country_code || undefined,
      product_id: productId,
      surface,
      subscribe_to_newsletter: subscribe,
    })

    // Store the number without its dial code — country_code is its own column,
    // so '+91 98765 43210' and '98765 43210' land in the table the same way.
    const normalised = normalisePhone(phone, form.country_code)
    const location = await lookUpLocation()

    try {
      const { error } = await supabase.from('contact_submissions').insert([
        {
          ...form,
          phone: normalised?.nationalNumber ?? phone.trim(),
          country_code: normalised?.country ?? form.country_code,
          location: location || null,
          product_id: productId ?? null,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
        },
      ])

      if (error) throw error

      if (subscribe) subscribeToNewsletter(name, email, surface)

      setStatus('success')
      track(ANALYTICS_EVENTS.CONTACT_FORM_SUCCEEDED, { product_id: productId, surface })
      startedRef.current = false
      blurredRef.current = new Set()
      setPhoneError('')
      // Keep the country they picked — it's still right for a second enquiry.
      setForm(prev => ({ ...EMPTY_FORM, country_code: prev.country_code }))
      onSuccess?.()
    } catch (e) {
      console.error(e)
      setStatus('error')
      track(ANALYTICS_EVENTS.CONTACT_FORM_FAILED, { reason: 'submit_error', surface })
    } finally {
      setLoading(false)
    }
  }, [form, subscribe, productId, surface, onSuccess])

  return (
    <div className={className}>
      {/* Alerts */}
      {status === 'success' && (
        <div className="mb-6 rounded-lg bg-green-50 p-3 text-sm text-green-700">
          Your message has been sent successfully. We’ll be in touch shortly.
        </div>
      )}
      {status === 'error' && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          Something went wrong. Please try again.
        </div>
      )}
      {formError && (
        <div role="alert" className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {formError}
        </div>
      )}

      {/* Form */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <input
          className={inputClass}
          placeholder="Name*"
          value={form.name}
          onChange={e => onChange('name', e.target.value)}
          onBlur={e => onBlurField('name', e.target.value)}
        />

        <input
          className={inputClass}
          placeholder="Email*"
          value={form.email}
          onChange={e => onChange('email', e.target.value)}
          onBlur={e => onBlurField('email', e.target.value)}
        />

        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <CountryCodeSelect
              className="shrink-0"
              value={form.country_code}
              onChange={onCountryChange}
            />
            <input
              className={twMerge(
                'min-w-0 flex-1',
                inputClass,
                phoneError && 'border-red-400 focus:ring-red-400',
              )}
              placeholder="Phone Number*"
              inputMode="tel"
              aria-invalid={!!phoneError}
              aria-describedby={phoneError ? 'phone-error' : undefined}
              value={form.phone}
              onChange={e => {
                onChange('phone', e.target.value)
                if (phoneError) setPhoneError('')
              }}
              onBlur={onPhoneBlur}
            />
          </div>
          {phoneError && (
            <span id="phone-error" role="alert" className="text-xs text-red-600">
              {phoneError}
            </span>
          )}
        </div>

        <input
          className={inputClass}
          placeholder="Number of Seats Required?*"
          value={form.seats}
          onChange={e => onChange('seats', e.target.value)}
          onBlur={e => onBlurField('seats', e.target.value)}
        />
      </div>

      {showProjectFields && (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          <ToggleGroup
            label="What type of chairs are needed?"
            options={CHAIR_TYPES}
            value={form.auditorium}
            onSelect={v => onSelectToggle('auditorium', v)}
          />

          <ToggleGroup
            label="Indoor or Outdoor seating?"
            options={ENVIRONMENTS}
            value={form.environment}
            onSelect={v => onSelectToggle('environment', v)}
          />
        </div>
      )}

      <textarea
        className={twMerge('mt-6 min-h-[120px] w-full', inputClass)}
        placeholder="Tell us more about your project"
        value={form.message}
        onChange={e => onChange('message', e.target.value)}
        onBlur={e => onBlurField('message', e.target.value)}
      />

      <label className="mt-6 flex cursor-pointer items-start gap-3 text-sm">
        <input
          type="checkbox"
          className="mt-0.5 size-4 shrink-0 cursor-pointer accent-my-deepOrange"
          checked={subscribe}
          onChange={e => setSubscribe(e.target.checked)}
        />
        <span>
          <span className="font-medium text-gray-900">Subscribe to our newsletter</span>
          <span className="block text-gray-500">Product updates and project stories. Unsubscribe anytime.</span>
        </span>
      </label>

      <div className="mt-8 flex justify-center">
        <Button variant="primary" onClick={onSubmit} disabled={loading}>
          {loading ? 'Sending…' : 'Send Message'}
        </Button>
      </div>
    </div>
  )
}
